/**
 * @fileoverview Reveal the current bank version for read-only inspection or the assigned verifier's task. Task summaries mask full numbers so readback is independent; drill-in views expose authorized values. Corrections are explicit versioned writes; `VerificationWorkflow.js` resets bank-dependent evidence while retaining the identity comparison. Server assignment capabilities govern every mutation; this component owns reveal identity and idempotent banking operations.
 */
import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import { AppButton, AppIcon } from '../shared';
import VerificationWorkflow from './VerificationWorkflow';
import ExistingBankingDetails from './ExistingBankingDetails';
import './BankingDetails.css';

function GuidedBankingDetails({ reviewId, result, matterNumber, clientId, onUpdated, onReload, readOnly = false }) {
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const generation = useRef(0);
  const operation = useRef(null);
  const context = matterNumber && clientId ? {matter_number:matterNumber,client_id:Number(clientId)} : {};
  const destination = result.submission.destination;
  async function load() {
    const current = generation.current;
    setLoading(true); setError(''); setBank(null);
    try {
      const {data} = await api.post(`/payment-instructions/${encodeURIComponent(reviewId)}/reveal`, {revision:result.revision,...context});
      if (generation.current === current) setBank(data.destination);
    } catch (failure) { if (generation.current === current) setError(failure.response?.data?.error || 'Bank details could not be loaded.'); }
    finally { if (generation.current === current) setLoading(false); }
  }
  useEffect(() => {
    generation.current += 1;
    if (result.can_reveal) load();
    return () => { generation.current += 1; };
    // Saved details arrive with their matching revision.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId, matterNumber, clientId]);
  async function act(action, method, evidence) {
    const current = generation.current;
    const payload = {action,revision:result.revision,banking_revision:bank.staffRevision || 0,...context,...evidence,...(action==='confirm'?{method}:{})};
    const key = JSON.stringify(payload);
    if (operation.current?.key !== key) operation.current = {key,id:crypto.randomUUID()};
    setBusy(true); setError(''); setNotice('');
    try {
      const {data} = await api.put(`/payment-instructions/${encodeURIComponent(reviewId)}/banking`, {...payload,operation_id:operation.current.id});
      if (generation.current !== current) return;
      setBank(data.destination); operation.current = null;
      setNotice(action==='save'?'Correction saved. Verify the updated numbers.':'Banking details verified');
      onUpdated(data);
    } catch (failure) { if (generation.current === current) setError(failure.response?.data?.error || 'Banking details could not be saved.'); }
    finally { if (generation.current === current) setBusy(false); }
  }
  const confirmed = !!(result.banking?.confirmed && bank && (result.banking.banking_revision ?? 0) === (bank.staffRevision || 0)
    && (!result.banking.version_id || result.banking.version_id === bank.staffVersionId));
  const canVerify = !readOnly && result.workflow?.can_verify && !confirmed;
  return <section className="banking-details" aria-label="Banking details">
    <header className="banking-details__header"><h3><AppIcon icon="pi pi-wallet" aria-hidden="true" />Banking details</h3>
      {confirmed ? <span className="banking-details__verified">Verified</span> : null}</header>
    <p className="banking-details__bank">{destination.bank_name} · {destination.account_type}</p>
    <p>Account holder: {destination.account_holder}</p>
    {result.workflow?.assignment ? <p>Assigned to <strong>{result.workflow.assignment.employee_name}</strong> · {result.workflow.amount_label}</p> : null}
    {loading ? <p role="status">Loading bank details…</p> : bank ? <>
      <div className="banking-details__numbers">
        <label>Routing number<strong>{canVerify ? '•••••' + bank.routingNumber.slice(-4) : bank.routingNumber}</strong></label>
        <label>Account number<strong>{canVerify ? '•••• ' + bank.accountNumber.slice(-4) : bank.accountNumber}</strong></label>
      </div>
      {canVerify ? <VerificationWorkflow reviewId={reviewId} result={result} bank={bank} busy={busy}
        onConfirm={(method,evidence)=>act('confirm',method,evidence)}
        onCorrect={(routing,account)=>act('save',null,{routing_number:routing,account_number:account})}
        onUpdated={onUpdated} matterNumber={matterNumber} clientId={clientId} /> : null}
    </> : !loading ? <p>Account ending {result.banking?.account_last4 || destination.account_last4}</p> : null}
    {error ? <div role="alert"><p>{error}</p><AppButton variant="secondary" onClick={onReload || load}>Reload details</AppButton></div> : null}
    {notice ? <p role="status">{notice}</p> : null}
  </section>;
}

export default function BankingDetails(props) {
  return props.result.workflow?.enabled === false
    ? <ExistingBankingDetails {...props} /> : <GuidedBankingDetails {...props} />;
}
